import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { FaRegTrashAlt, FaRegEdit } from "react-icons/fa";
import { MdAdd, MdClose, MdCheck, MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import { ImSpinner2 } from "react-icons/im";
import { FiImage, FiHelpCircle, FiDownload } from "react-icons/fi";
import PanelSkeleton from "../PanelSkeleton";
import mediaUrl from "../../utils/mediaUrl";
import store from "../../redux/store";
import DEFAULT_FAQS from "../../data/defaultFaqs";
import styles from "../../styles/categoriesPanel.module.css";
import faqStyles from "../../styles/faqsPanel.module.css";

const authConfig = (extra = {}) => {
  const token = store.getState().auth?.token;
  return {
    withCredentials: true,
    ...extra,
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra.headers },
  };
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api/faqs`;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const DEFAULT_FORM = { question: "", answer: "" };

// "اعرف اكثر عن لوحاتنا الفنية" — the FAQ shown on every product page
const FaqsPanel = () => {
  const [faqs, setFaqs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [reordering, setReordering] = useState(false);
  const [importing, setImporting]   = useState(false);
  const [editId, setEditId]         = useState(null);
  const [form, setForm]             = useState(DEFAULT_FORM);
  const [savedImage, setSavedImage] = useState(null);   // current image url (edit)
  const [imageFile, setImageFile]   = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage]   = useState(false);
  const [showForm, setShowForm]     = useState(false);
  const questionRef = useRef(null);
  const imgRef = useRef(null);

  const fetchFaqs = async () => {
    try {
      const res = await axios.get(`${API}/dashboardFetch`, authConfig());
      setFaqs(res.data?.data ?? []);
    } catch {
      toast.error(<span className="toast">فشل تحميل الأسئلة</span>);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFaqs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setEditId(null);
    setForm(DEFAULT_FORM);
    setSavedImage(null);
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
  };

  const openAdd = () => {
    resetForm();
    setShowForm(true);
    setTimeout(() => questionRef.current?.focus(), 80);
  };

  const openEdit = (faq) => {
    resetForm();
    setEditId(faq.id);
    setForm({ question: faq.question, answer: faq.answer });
    setSavedImage(faq.imageUrl ?? null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => questionRef.current?.focus(), 80);
  };

  const closeForm = () => {
    resetForm();
    setShowForm(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(<span className="toast">حجم الصورة يجب أن يكون أقل من 5 ميجابايت</span>);
      return;
    }
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setRemoveImage(false);
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
    if (savedImage) setRemoveImage(true);
  };

  const currentImage = imagePreview || (!removeImage && savedImage ? mediaUrl(savedImage) : null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.question.trim() || !form.answer.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("question", form.question.trim());
      fd.append("answer", form.answer.trim());
      if (imageFile) fd.append("imageFile", imageFile);
      else if (removeImage) fd.append("removeImage", "true");

      if (editId) {
        await axios.put(`${API}/${editId}`, fd, authConfig());
        toast.success(<span className="toast">تم تعديل السؤال</span>);
      } else {
        await axios.post(API, fd, authConfig());
        toast.success(<span className="toast">تم إضافة السؤال</span>);
      }
      closeForm();
      fetchFaqs();
    } catch {
      toast.error(<span className="toast">فشلت العملية. حاول مرة أخرى</span>);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (faq) => {
    confirmAlert({
      message: `حذف السؤال "${faq.question}" ؟`,
      buttons: [
        {
          label: "نعم",
          onClick: async () => {
            setDeleting(true);
            try {
              await axios.delete(`${API}/${faq.id}`, authConfig());
              toast.success(<span className="toast">تم حذف السؤال</span>);
              if (editId === faq.id) closeForm();
              fetchFaqs();
            } catch {
              toast.error(<span className="toast">فشل الحذف. حاول مرة أخرى</span>);
            } finally {
              setDeleting(false);
            }
          },
        },
        { label: "لا" },
      ],
    });
  };

  // Copy the questions currently hard-coded on the product page into the dashboard
  const importDefaults = async () => {
    setImporting(true);
    try {
      for (const faq of DEFAULT_FAQS) {
        const fd = new FormData();
        fd.append("question", faq.question);
        fd.append("answer", faq.answer);
        await axios.post(API, fd, authConfig());
      }
      toast.success(<span className="toast">تم استيراد الأسئلة الحالية</span>);
      fetchFaqs();
    } catch {
      toast.error(<span className="toast">فشل الاستيراد. حاول مرة أخرى</span>);
      fetchFaqs();
    } finally {
      setImporting(false);
    }
  };

  const move = async (index, direction) => {
    const next = [...faqs];
    const swapIdx = direction === "up" ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
    setFaqs(next);

    setReordering(true);
    try {
      await axios.put(
        `${API}/reorder/batch`,
        { items: next.map((faq, i) => ({ id: faq.id, sortOrder: i })) },
        authConfig()
      );
    } catch {
      toast.error(<span className="toast">فشل حفظ الترتيب. حاول مرة أخرى</span>);
      fetchFaqs();
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className={styles.panel}>

      {/* ── Header ── */}
      <div className={styles.panelHead}>
        <span className={styles.panelTitle}>
          <FiHelpCircle className={styles.panelTitleIcon} />
          الأسئلة الشائعة
        </span>
        <button className={styles.addBtn} onClick={openAdd} disabled={showForm}>
          <MdAdd /> إضافة سؤال
        </button>
      </div>

      {/* ── Add / Edit Form ── */}
      {showForm && (
        <form className={styles.form} onSubmit={handleSubmit} encType="multipart/form-data">
          <div className={styles.formHead}>
            <span className={styles.formTitle}>{editId ? "تعديل السؤال" : "سؤال جديد"}</span>
            <button type="button" className={styles.formClose} onClick={closeForm}>
              <MdClose />
            </button>
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>السؤال *</label>
            <input
              ref={questionRef}
              className={styles.formInput}
              placeholder="مثال: هل اللوحات جاهزة للتعليق؟"
              maxLength={255}
              value={form.question}
              onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
              required
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>الإجابة *</label>
            <textarea
              className={`${styles.formInput} ${faqStyles.textarea}`}
              placeholder="اكتب الإجابة..."
              maxLength={5000}
              rows={4}
              value={form.answer}
              onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
              required
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>صورة توضيحية للإجابة (اختياري)</label>
            {currentImage ? (
              <div className={faqStyles.imageRow}>
                <div className={styles.imgPreviewWrap} style={{ width: "10rem", height: "6.5rem" }}>
                  <img src={currentImage} alt="" className={styles.imgPreview} />
                  <button type="button" className={styles.imgRemoveBtn} onClick={clearImage} title="إزالة الصورة">
                    <MdClose />
                  </button>
                </div>
                <button type="button" className={styles.imgPickBtn} onClick={() => imgRef.current?.click()}>
                  <FiImage className={styles.imgPickIcon} />
                  تغيير الصورة
                </button>
              </div>
            ) : (
              <button type="button" className={styles.imgPickBtn} onClick={() => imgRef.current?.click()}>
                <FiImage className={styles.imgPickIcon} />
                اختيار صورة
              </button>
            )}
            <input type="file" accept="image/*" ref={imgRef} onChange={handleImageChange} style={{ display: "none" }} />
          </div>

          <div className={styles.formActions}>
            <button type="submit" className={styles.saveBtn} disabled={saving}>
              {saving ? <ImSpinner2 className="smallspinner" /> : <><MdCheck /> حفظ</>}
            </button>
            <button type="button" className={styles.cancelFormBtn} onClick={closeForm}>
              إلغاء
            </button>
          </div>
        </form>
      )}

      {/* ── List ── */}
      {loading ? (
        <PanelSkeleton count={3} />
      ) : faqs.length === 0 ? (
        <div className={faqStyles.empty}>
          <p className="warn">
            لا توجد أسئلة بعد — تظهر الأسئلة الافتراضية ({DEFAULT_FAQS.length}) في صفحة اللوحة حتى تضيف أسئلتك.
          </p>
          <button className={styles.addBtn} onClick={importDefaults} disabled={importing}>
            {importing ? <ImSpinner2 className="smallspinner" /> : <><FiDownload /> استيراد الأسئلة الحالية لتعديلها</>}
          </button>
        </div>
      ) : (
        <div className={styles.list}>
          {faqs.map((faq, i) => (
            <div key={faq.id} className={styles.row} style={{ animationDelay: `${i * 0.04}s` }}>
              <div className={styles.orderBtns}>
                <button
                  className={styles.orderBtn}
                  onClick={() => move(i, "up")}
                  disabled={i === 0 || reordering || deleting}
                  title="تحريك لأعلى"
                >
                  <MdKeyboardArrowUp />
                </button>
                <button
                  className={styles.orderBtn}
                  onClick={() => move(i, "down")}
                  disabled={i === faqs.length - 1 || reordering || deleting}
                  title="تحريك لأسفل"
                >
                  <MdKeyboardArrowDown />
                </button>
              </div>

              {faq.imageUrl && (
                <div className={styles.thumb}>
                  <img src={mediaUrl(faq.imageUrl)} alt="" className={styles.thumbImg} />
                </div>
              )}

              <div className={faqStyles.info}>
                <span className={styles.catName}>{faq.question}</span>
                <span className={faqStyles.answer}>{faq.answer}</span>
              </div>

              <span className={styles.orderBadge}>{i + 1}</span>

              <div className={styles.actions}>
                <button className={styles.editBtn} onClick={() => openEdit(faq)} disabled={deleting} title="تعديل">
                  <FaRegEdit />
                </button>
                <button
                  className={styles.deleteBtn}
                  onClick={() => !deleting && handleDelete(faq)}
                  disabled={deleting}
                  title="حذف"
                >
                  {deleting ? <ImSpinner2 className="smallspinner" /> : <FaRegTrashAlt />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className={styles.hint}>
        تظهر هذه الأسئلة في قسم "اعرف اكثر عن لوحاتنا الفنية" في صفحة كل لوحة، بنفس الترتيب هنا.
      </p>
    </div>
  );
};

export default FaqsPanel;
